import * as am5 from '@amcharts/amcharts5';

import { AxisRendererX, AxisRendererY, ColumnSeries, IAxisRendererYSettings, XYChart } from '@amcharts/amcharts5/xy';
import { Am5CategoryValueXYBaseChart } from '../am5-category-value-x-y-base-chart.model';
import { Am5CategoryValueXYBarChartConfiguration } from './am5-category-value-x-y-bar-chart-configuration.model';
import { Am5CategoryValueXYBarChartSettings } from './am5-category-value-x-y-bar-chart-settings.model';
import { chartColors } from '../../../../../Common/Charts/enums/chart-colors.enum';
import { ChartModuleTypesEnum } from '../../../../../Common/Charts/enums/chart-module-types.enum';
import { ChartSeries } from '../../chart-series.model';
import { ChartSizeHelper } from '../../../../../Common/Charts/helpers/chart-size.helper';
import { DataPointModel } from '../../data-point.model';
import { LabelDisplayOptionsEnum } from '../../../../../Common/Misc/enums/label-display-options.enum';


export class Am5CategoryValueXYBarChart extends Am5CategoryValueXYBaseChart {

    override config: Am5CategoryValueXYBarChartConfiguration;
    override settings: Am5CategoryValueXYBarChartSettings;

    override categoryAxisRenderer = AxisRendererY;

    valueLabelWidth: number = 0;
    valueLabelExtraMaxAdjust: number = -0.05;
    valueLabelTemplate: am5.Template<am5.Label>;
    savedHeight: string;

    readonly smallBarHeight = 30;
    readonly mediumBarHeight = 40;
    readonly largeBarHeight = 50;

    chartBlockContainer: HTMLElement | undefined;
    readonly smallChartDisplayLimit = 3;
    private blockContainerObserver: ResizeObserver | undefined;

    constructor(
        series: ChartSeries[],
        config: Am5CategoryValueXYBarChartConfiguration,
        settings: Am5CategoryValueXYBarChartSettings
    ) {
        super(series, config, settings);
    }

    public initChart(element: HTMLElement, chartType = XYChart): void {
        super.initChart(element, chartType);

        this.setUpScrollbarX();
        this.setUpValueLabelTemplate();
        if (this.config.showFewerItemsAtLimitedSpace) {
            this.findChartBlockContainer(element);
            this.setUpBlockContainerResizeObserver();
        }
    }

    // Walks up from the chart's own element to find the surrounding report-block wrapper, so its
    // live height (not the chart's, which amCharts5 sizes to fit its content) can be checked against
    // smallChartHeight to decide whether to show fewer bars.
    findChartBlockContainer(element: HTMLElement): void {
        let el = element;
        while (
            el?.parentElement
            && !(
                el.classList.contains('agpr-block-content')
                || el.classList.contains('agpr-chartBlock-dashboard')
                || el.classList.contains('agpr-slide-chart')
                || el.classList.contains('agpr-chartDrillDownDialog-chartArea')
            )
        ) {
            el = el.parentElement;
        }
        this.chartBlockContainer = el;
    }

    setUpBlockContainerResizeObserver(): void {
        if (!this.chartBlockContainer) {
            return;
        }
        this.blockContainerObserver?.disconnect();
        this.blockContainerObserver = new ResizeObserver(() => {
            if (this.amchart) {
                this.setUpSeries(this.modifyDataArray());
            }
        });
        this.blockContainerObserver.observe(this.chartBlockContainer);
    }

    createCategoryAxisRenderer(rendererSettings: IAxisRendererYSettings): AxisRendererY {
        rendererSettings.inside = this.config.stackedCategoryLabel;
        rendererSettings.cellStartLocation = 0.2;
        rendererSettings.cellEndLocation = 0.7;
        const renderer = AxisRendererY.new(this.root, rendererSettings);
        const categoryLabel = renderer.labels.template;
        if (this.config.stackedCategoryLabel) {
            categoryLabel.setAll({
                centerY: am5.p100,
                paddingLeft: 0
            });
            categoryLabel.adapters.add('location', () => this.getStackedCategoryLabelLocation());
        }

        return renderer;
    }

    createValueAxisRenderer(rendererSettings: IAxisRendererYSettings): AxisRendererX {
        const renderer = AxisRendererX.new(this.root, rendererSettings);
        renderer.grid.template.set('visible', false);
        renderer.labels.template.set('visible', false);

        return renderer;
    }

    public override setUpTooltip(series: ChartSeries, seriesIndex: number): am5.Tooltip {
        const tooltip = super.setUpTooltip(series, seriesIndex);
        tooltip.set('pointerOrientation', this.settings.tooltipPointerOrientation);
        return tooltip;
    }

    public override getColumnTooltipX(): number | am5.Percent {
        return 10;
    }

    addAm5Series(chartSeries: ChartSeries, seriesIndex: number, settings: any): ColumnSeries {
        const setting = Object.assign(
            settings,
            {
                xAxis: this.valueAxis,
                yAxis: this.categoryAxis,
                categoryYField: 'label',
                valueXField: 'value' + seriesIndex
            }
        );
        const series = ColumnSeries.new(this.root, setting);
        if (this.config.stacked) {
            // todo: later charts
            series.columns.template.setAll({
                // height: am5.percent(this.rawData?.length > 8 ? 75 : 100)
            });
        } else {
            series.columns.template.setAll({
                cornerRadiusTL: 2,
                cornerRadiusBL: 2,
                cornerRadiusTR: 2,
                cornerRadiusBR: 2,
                maxHeight: this.config.barHeight
            });
        }

        if (!this.config.hideValueLabel) {
            this.setUpLabelBullet(series, seriesIndex, chartSeries);
        }

        return series;
    }

    setUpLabelBullet(series: ColumnSeries, seriesIndex: number, chartSeries: ChartSeries): void {
        let valueLabelText = '';
        this.valueLabelWidth = 0;
        if (this.settings.enableDataDisplaySettings) {
            if (this.settings.labelDisplayOptions) {
                const doCount = this.settings.labelDisplayOptions.includes(LabelDisplayOptionsEnum.count);
                const doPercent = this.settings.labelDisplayOptions.includes(LabelDisplayOptionsEnum.percent) && !this.config.noPercentage;
                const symbol = this.settings.symbol || '';

                if (doCount) {
                    valueLabelText += `[font-style:italic]{value${seriesIndex}.formatNumber(\'#.#a\')}${symbol}[/]`;
                    this.valueLabelWidth += 30;
                }
                if (doCount && doPercent) {
                    valueLabelText += ', ';
                    this.valueLabelWidth += 5;
                }
                if (doPercent) {
                    valueLabelText += `[bold]{percent${seriesIndex}.formatNumber(\'#.0\')}%[/]`;
                    this.valueLabelWidth += 25;
                }
            }
        }

        if (!valueLabelText) {
            return;
        }

        series.bullets.push(() => {
            const labelSettings: any = {
                text: valueLabelText
            };
            const sprite = am5.Label.new(this.root, labelSettings, this.valueLabelTemplate);

            return am5.Bullet.new(this.root, {
                locationX: 1,
                locationY: 0.5,
                sprite: sprite
            },);
        });

        this.valueAxis.adapters.add('extraMax', () => {
            const plotWidth = this.amchart.plotContainer.width();
            return this.valueLabelWidth / (plotWidth - this.valueLabelWidth) || 0;
        });
    }

    public override setUpExporting(): void {
        super.setUpExporting();

        this.exporting.events.on('exportstarted', () => {
            if (this.canFixRowHeight()) {
                this.savedHeight = this.root.dom.style.height;
                this.setFixedRowHeight(65);
            }
        });
        this.exporting.events.on('dataprocessed', function(event) {
            event.data = event.data.reverse();
        });
    }

    protected override restoreExportState(): void {
        super.restoreExportState();
        if (this.savedHeight !== undefined) {
            this.root.dom.style.height = this.savedHeight;
            this.savedHeight = undefined;
        }
    }

    canFixRowHeight(): boolean {
        return this.rawData?.length < 10 && this.rawData?.length > 0 && !!this.config.columnHeight;
    }

    setFixedRowHeight(cellSize: number): void {
        const paddingTop = this.config.padding?.customPadding?.top || (this.config.padding?.noPadding ? 0 : 15);
        const headerHeight = this.header?.height() || 55;
        this.root.dom.style.height = paddingTop + headerHeight + this.rawData.length * cellSize + 'px';
    }

    setUpScrollbarX(): void {
        // todo: ga chart
        if (this.config.includeScrollbarX && this.isBarChart() && this.config.moduleType !== ChartModuleTypesEnum.SHARED_REPORTS) {
            const scrollbar = am5.Scrollbar.new(this.root, {
                orientation: 'horizontal',
                dx: 15
            });
            scrollbar.startGrip.set('scale', 0.75);
            scrollbar.endGrip.set('scale', 0.75);
            this.amchart.set('scrollbarX', scrollbar);
            this.amchart.zoomOutButton.set('disabled', true);
        }
    }

    setUpValueLabelTemplate(): void {
        this.valueLabelTemplate = am5.Template.new({
            centerY: am5.p50,
            populateText: true,
            oversizedBehavior: 'none',
            fontFamily: 'Poppins, Arial, sans-serif',
            fontSize: '0.75rem',
            fill: am5.color(chartColors.agprBlack)
        });

        if (this.config.stackedCategoryLabel) {
            this.valueLabelTemplate.adapters.add('fontSize', () => this.getValueLabelFontSize());
            this.valueLabelTemplate.adapters.add('centerY', () => this.getValueLabelCenterY());
        }
    }

    getValueLabelFontSize(): string {
        return this.isSmallBarHeight() ? '0.6rem' : '0.75rem';
    }

    getValueLabelCenterY(): am5.Percent {
        return this.isSmallBarHeight() ? am5.percent(48) : am5.p50;
    }

    public override updateData(data: DataPointModel[]): void {
        super.updateData(data);
        this.refreshBarHeightDependentSettings();
    }

    refreshBarHeightDependentSettings(): void {
        if (!this.config.stackedCategoryLabel) {
            return;
        }
        this.categoryAxis.get('renderer').labels.template.set('location', this.getStackedCategoryLabelLocation());
        this.valueLabelTemplate?.setAll({
            fontSize: this.getValueLabelFontSize(),
            centerY: this.getValueLabelCenterY()
        });
    }

    modifyDataArray(): DataPointModel[] {
        const dataPoints = super.modifyDataArray();
        const displayLimit = this.config.barDisplayLimit || 12;

        if (this.config.showFewerItemsAtLimitedSpace && this.chartBlockContainer) {
            const containerHeight = parseInt(getComputedStyle(this.chartBlockContainer).height, 10);
            if (containerHeight < ChartSizeHelper.getCellHeight()) {
                return dataPoints.slice(-(this.config.smallChartDisplayLimit || this.smallChartDisplayLimit));
            }
        }

        return dataPoints.slice(-displayLimit);
    }

    getStackedCategoryLabelLocation(): number {
        const barHeight = this.getBarHeight();
        if (barHeight <= this.smallBarHeight) {
            return 0.5;
        } else if (barHeight <= this.mediumBarHeight) {
            return 0.56;
        } else if (barHeight <= this.largeBarHeight) {
            return 0.62;
        } else {
            return 0.68;
        }
    }


    isSmallBarHeight(): boolean {
        return this.getBarHeight() <= this.smallBarHeight;
    }

    getBarHeight(): number {
        const renderer = this.categoryAxis?.get('renderer') as AxisRendererY;
        if (!renderer) {
            return 0;
        }
        const barCount = this.categoryAxis.dataItems.length || 1;

        return renderer.axisLength() / barCount;
    }


    public override destroy(): void {
        this.blockContainerObserver?.disconnect();
        super.destroy();
    }

    public clone(): Am5CategoryValueXYBarChart {
        const clone = new Am5CategoryValueXYBarChart(
            this.series.map(s => JSON.parse(JSON.stringify(s))),
            this.config,
            this.settings
        );

        clone.series.forEach((s, index) => {
            s.getSeriesUrl = this.series[index].getSeriesUrl?.bind(s);
        });

        return clone;
    }
}
